import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Reveal, Overline } from "@/components/Reveal";

const IMG = "https://images.unsplash.com/photo-1625690303837-654c9666d2d0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800&auto=format&fit=crop";

const sections = [
  {
    num: "01",
    title: "Business Development",
    body: "ADD VALUE HOLDINGS LLC is continuously identifying and developing business opportunities — from consumer brands to digital products. Each venture is evaluated against a single standard: it must be built to add real, lasting value.",
  },
  {
    num: "02",
    title: "Brand Development",
    body: "The company develops its own brands from concept to launch — naming, identity, product, and story. ADD VALUE Clothing is the first of these; it will not be the last. Every brand is built inside the holding company and held to the same standard.",
  },
  {
    num: "03",
    title: "E-Commerce",
    body: "Direct-to-customer retail is a core part of the company's future. The ADD VALUE Clothing online store is currently in development and will serve as the foundation for the company's wider e-commerce operations as new products launch.",
  },
  {
    num: "04",
    title: "Future Ventures",
    body: "Several concepts are under evaluation and development inside the company. Details are shared when ventures are ready — not before. What is announced here will always be real, launched, and accountable.",
  },
  {
    num: "05",
    title: "Strategic Partnerships",
    body: "The right partnerships multiply what is possible. ADD VALUE welcomes conversations with aligned founders, creators, and operators who share the same long-term philosophy. If you are building something with substance, we want to hear about it.",
  },
];

const principles = [
  { t: "Deliberate", d: "Few things, done properly. We do not chase every trend." },
  { t: "Aligned", d: "Partners and projects must share the philosophy, not just the upside." },
  { t: "Long-term", d: "We build for durability, not for headlines." },
];

const Business = () => {
  useEffect(() => {
    document.title = "Business & Ventures — ADD VALUE HOLDINGS LLC";
  }, []);

  return (
    <div data-testid="business-page">
      <PageHero
        testid="business-hero"
        image={IMG}
        overline="Business & Ventures"
        lines={["Building what", "comes next."]}
        lede="ADD VALUE HOLDINGS LLC develops businesses, brands, products, and future ventures — deliberately, and with a long view."
      />

      <section className="border-t border-[#1c1c1f]">
        <div className="mx-auto max-w-7xl px-6 py-24 sm:px-8 lg:px-12 lg:py-32">
          <Reveal className="mb-16">
            <Overline>Areas of Development</Overline>
          </Reveal>
          <div>
            {sections.map((s, i) => (
              <Reveal key={s.num} delay={0.05 * i}>
                <div
                  data-testid={`business-section-${s.num}`}
                  className="grid gap-6 border-t border-[#222226] py-12 lg:grid-cols-12 lg:gap-12 lg:py-16"
                >
                  <div className="lg:col-span-4">
                    <p className="font-mono text-[10px] tracking-[0.35em] text-[#D4AF37]">
                      {s.num}
                    </p>
                    <h2 className="mt-4 font-serif text-2xl tracking-tight text-white sm:text-3xl">
                      {s.title}
                    </h2>
                  </div>
                  <p className="max-w-2xl text-sm font-light leading-relaxed text-stone-400 sm:text-base lg:col-span-8 lg:pt-8">
                    {s.body}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-[#1c1c1f] bg-[#0d0d0f]">
        <div className="mx-auto max-w-7xl px-6 py-24 sm:px-8 lg:px-12 lg:py-32">
          <Reveal className="mb-14">
            <Overline>How We Work</Overline>
            <h2 className="mt-6 font-serif text-2xl tracking-tight text-white/95 sm:text-3xl lg:text-4xl">
              Three principles behind every decision.
            </h2>
          </Reveal>
          <div className="grid gap-px border border-[#222226] bg-[#222226] md:grid-cols-3">
            {principles.map((p, i) => (
              <Reveal key={p.t} delay={0.08 * i} className="bg-[#0d0d0f]">
                <div
                  data-testid={`principle-${i + 1}`}
                  className="h-full p-8 transition-colors duration-500 hover:bg-[#121214] lg:p-10"
                >
                  <h3 className="font-serif text-2xl text-white">{p.t}</h3>
                  <div className="mt-4 h-px w-10 bg-[#D4AF37]/60" />
                  <p className="mt-4 text-sm font-light leading-relaxed text-stone-500">
                    {p.d}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2} className="mt-16 flex flex-wrap items-center justify-between gap-8 border border-[#222226] bg-[#0A0A0B] p-8 lg:p-12">
            <div>
              <h3 className="font-serif text-2xl text-white sm:text-3xl">
                Have something worth building?
              </h3>
              <p className="mt-3 max-w-xl text-sm font-light text-stone-400">
                Partnership and venture inquiries start with a conversation.
              </p>
            </div>
            <Link
              data-testid="business-contact-button"
              to="/contact"
              className="group inline-flex items-center gap-3 bg-white px-8 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-black transition-colors duration-300 hover:bg-[#D4AF37]"
            >
              Contact Us
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-1 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Business;
